import styled from "styled-components";
import Block, { BlockContent } from "~/components/Block";
import Panel from "~/components/Panel";
import {
  getCommonSliceProps,
  getRichText,
  resolveMedia,
  richTextIsEmpty,
} from "~/external/prismic";
import connectSlice from "~/lib/connectSlice";
import { vSpacing } from "~/styles/mixins";
import { smallerThan } from "~/styles/theme";

export const PanelGrid = styled.div`
  ${vSpacing};
  display: grid;
  grid-template-columns: repeat(${(p) => p.cols || "4"}, 1fr);
  grid-gap: ${(p) => p.theme.sizes.vSpacing * 8}rem
    ${(p) => p.theme.sizes.hSpacing * 2}rem;

  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    grid-template-columns: 1fr;
  }
`;

function PanelGridSlice(props) {
  const {
    spacingTop = 0,
    spacingBottom = 6,
    highlightColor,
    color,
    backgroundColor,
    animateIn = false,
    media,
    product,
    variantKey,
    title,
    subtitle,
    text,
    buttonText,
    href,
    hrefAs,
    hrefTarget,
    hasTopDivider,
    textWidth = "half",
    alignImage = "right",
    buttonType = "outline",
    buttonAction = "link",
    disableImageLink = false,
  } = props;

  return (
    <Block
      spacingTop={spacingTop}
      spacingBottom={spacingBottom}
      backgroundColor={backgroundColor}
      color={color}
    >
      <BlockContent>
        <PanelGrid cols={1}>
          <Panel
            color={color}
            title={title}
            subtitle={subtitle}
            text={text}
            media={media}
            buttonText={buttonText}
            href={href}
            hrefAs={hrefAs}
            hrefTarget={hrefTarget}
            fullWidthText={textWidth === "full"}
            buttonType={buttonType}
            buttonAction={buttonAction}
            hasTopDivider={hasTopDivider}
            alignImage={alignImage}
            product={product}
            variantKey={variantKey}
            disableImageLink={disableImageLink}
          />
        </PanelGrid>
      </BlockContent>
    </Block>
  );
}

export function mapSliceToProps(slice, externalData) {
  const { primary, items = [] } = slice;

  const { products = [], configurations } = externalData;

  // console.log('configurations', configurations);

  const props = {
    ...getCommonSliceProps(slice),

    media: resolveMedia(primary, primary.video_aspect_ratio || "1:1"),
    title: primary.title,
    subtitle: primary.subtitle,
    text: getRichText(primary.text),

    product:
      primary.product.uid && products.find((pr) => pr.key === primary.product.data.key),

    // Resolve variantKey: prefer product.data.color, fall back to legacy ctp_variant_key field
    variantKey: (() => {
      if (primary.product?.type === "product" && primary.product.data?.color) {
        const ctpProduct = products.find((pr) => pr.key === primary.product.data.key);
        const normalizedColor = primary.product.data.color.trim().toLowerCase();
        const colorVariant = ctpProduct?.variants?.find(
          (v) => v.color?.slug?.toLowerCase() === normalizedColor
        );
        if (colorVariant) return colorVariant.key;
      }
      return primary.ctp_variant_key || undefined;
    })(),

    hasTopDivider: primary.top_divider === "visible",
    textWidth: primary.text_width && primary.text_width.toLowerCase(),
    alignImage: primary.align_image && primary.align_image.toLowerCase(),
    buttonText: primary.button_text,
    buttonType: primary.button_type && primary.button_type.toLowerCase(),
    buttonAction: primary.button_action && primary.button_action.toLowerCase(),
    disableImageLink: primary.disable_image_link,
  };
  // get product or configuration
  if (primary.product.type === "product") {
    // product
    props.product = products.find((pr) => pr.key === primary.product.data.key);
  } else if (primary.product.type === "variant") {
    // configuration
    // console.log('FETCH VARIANT', primary.product);
    // find the configuration that matches the uid!
    const configuration = configurations.find((c) => c.uid === primary.product.uid);
    if (configuration) {
      // console.log(configuration);
      props.product = configuration;
      // set title, if not defined
      if (!primary.title) {
        props.title = configuration.data.product_title;
      } else {
        // set title, if empty
        if (props.title.length === 0) {
          props.title = configuration.data.product_title;
        }
      }

      // set descriotion
      if (configuration.data.description) {
        if (richTextIsEmpty(primary.text)) {
          props.text = getRichText(configuration.data.description);
        }
      }
      if (configuration.data.image) {
        // media
        props.media = [
          configuration.data.image.url,
          "image",
          configuration.data.image.width,
          configuration.data.image.height,
        ];
      }
    }
  }

  return props;
}

export default connectSlice(mapSliceToProps)(PanelGridSlice);
